import axios from "axios";

import { apiConfig } from "../config/authConfig";

import { IVendor, IVendorContact, IVendorCustomer, IVendorDetails, IVendorExportRequest, IVendorLight } from "../types/vendor";
import { IFuzzySearchRequest } from "../types/system";
import { IAddress } from "../types/base";

import { CredentialManager } from "../classes/credentialManager";
import { DocumentService } from "./documentService";
export namespace VendorService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one response object to one Customer.
	 * @param data Customer response data from an API.
	 **********/
	export function mapVendor(data: any) {
		let item: IVendor = {
			contact: data.contact,
			details: data.details,
			documents: DocumentService.mapDocuments(data.documents),
			id: data.id,
			mailingAddress: data.mailingAddress,
			physicalAddress: data.physicalAddress,
			customers: data.customers,
			materials: data.materials,
		}

		return item;
	}

	/**********
	 * Convert an array response object to an array of Customers.
	 * @param data Customer response data from an API.
	 **********/
	export function mapVendors(data: any) {
		let vendors: IVendor[] = [];

		if (Array.isArray(data) && data.length > 0) {

		}
		data.map((item: any) => {
			vendors.push(mapVendor(item));
		})

		return vendors;
	}

	/**********
	 * Convert one response object to one Customer.
	 * @param data Customer response data from an API.
	 **********/
	export function mapVendorLight(data: any) {
		let item: IVendorLight = {
			id: data.id,
			name: data.name,
			city: data.city,
			state: data.state,
			status: data.status,
			usesExternalPriceIds: data.usesExternalPriceIds,
			externalProperties: data.externalProperties,
			minimumLoadWeight: data.minimumLoadWeight,
			zeroWeightLoadNotifications: data.zeroWeightLoadNotifications,
			requiresEscalatedDateChangePermission: data.requiresEscalatedDateChangePermission,
		}

		return item;
	}

	/**********
	 * Convert an array response object to an array of Customers.
	 * @param data Customer response data from an API.
	 **********/
	export function mapVendorLightList(data: any) {
		let vendors: IVendorLight[] = [];

		if (Array.isArray(data) && data.length > 0) {

		}
		data.map((item: any) => {
			vendors.push(mapVendorLight(item));
		})

		return vendors;
	}

	/**********
	 * Gets all vendors.
	 **********/
	export async function getVendors() {
		const apiResponse = axios.get(
			apiConfig.default + "/vendor/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all active vendors.
	 **********/
	export async function getActiveVendors() {
		const apiResponse = axios.get(
			apiConfig.default + "/vendor/all/active",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all vendors by customer id.
	 * * @param data Customer response data from an API.
	 **********/
	export async function getVendorsByCustomerId(customerId: number) {
		const apiResponse = axios.get(
			apiConfig.default + "/vendor" + (customerId ? "/customer/" + customerId : "/all"),
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a vendor.
	 * @param vendorId The id of the vendor.
	 **********/
	export async function getVendor(vendorId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/vendor/" + vendorId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets a plain vendor without any of its joined tables.
	 * @param vendorId The id of the vendor.
	 **********/
	export async function getOnlyVendor(vendorId: string) {
		const apiResponse = axios.get(
			apiConfig.default + "/vendor/" + vendorId + "/only",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Gets all vendors by customer id and transporters associated with those vendors..
	 * @param customerId The customer id to filter vendors by.
	 * @param currentVendorId The current vendor id for the load or shipment being edited.
	 **********/
	export async function getVendorTransporterValueSetsByCustomerId(customerId: number, currentVendorId: number) {
		const apiResponse = axios.get(
			apiConfig.default + `/vendor/transporter/values/customer/${customerId}/current/${currentVendorId}`,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Adds a vendor.
	 * @param details The vendor's details.
	 **********/
	export async function addVendor(details: IVendorDetails) {
		const apiResponse = axios.put(
			apiConfig.default + "/vendor",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Adds a vendor.
	 * @param vendorId The id of the vendor.
	 * @param details The vendor's details.
	 **********/
	export async function setDetails(vendorId: string, details: IVendorDetails) {
		const apiResponse = axios.put(
			apiConfig.default + "/vendor/" + vendorId + "/details",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a vendors's contact.
	 * @param vendorId The vendor's id.
	 * @param contact The vendor's contact.
	 **********/
	export async function setContact(vendorId: string, contact: IVendorContact) {
		const apiResponse = axios.put(
			apiConfig.default + "/vendor/" + vendorId + "/contact",
			contact,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update a vendor's addresses.
	 * @param vendorId The vendor's id.
	 * @param physicalAddress The vendor's physical address.
	 * @param mailingAddress The vendor's mailing address.
	 **********/
	export async function setAddresses(vendorId: string, physicalAddress: IAddress, mailingAddress: IAddress) {
		const apiResponse = axios.put(
			apiConfig.default + "/vendor/" + vendorId + "/addresses",
			{
				physicalAddress: physicalAddress,
				mailingAddress: mailingAddress
			},
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Add a vendor customer relationship.
	 * @param request VendorCustomer relationship information.
	 **********/
	export async function addCustomer(request: IVendorCustomer) {
		const apiResponse = axios.put(
			apiConfig.default + "/vendor/customer",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Remove a vendor customer relationship.
	 * @param vendorId The vendors's id.
	 * @param customerId The customer's id.
	 **********/
	export async function removeCustomer(vendorId: string, customerId: string) {
		const apiResponse = axios.delete(
			apiConfig.default + "/vendor/" + vendorId + "/customer/" + customerId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
     * Search for duplicate existing vendor names using a fuzzy compare method.
     **********/
	export async function fuzzySearchForDuplicateVendorNames(request: IFuzzySearchRequest) {
		const apiResponse = axios.post(
			apiConfig.default + "/vendor/fuzzy/vendorname",
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Exports the entire filtered vendor list to an Excel document.
	 * @param request The export request parameters containing filter and sort options.
	 **********/
	export async function exportVendorListToExcel(request: IVendorExportRequest) {
		const apiResponse = axios.post(
			apiConfig.default + `/vendor/export`,
			request,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}
}
